<template>
  <section class="banner__two slider-two position-relative">
    <div class="pattern-layer">
      <div
        class="pattern-1 position-absolute"
        data-parallax='{"x": 100}'
        :style="`background-image: url('${patternImage}')`"
      ></div>
    </div>
    <div class="image-layer position-absolute" :style="`background-image: url('${backgroundImage}')`"></div>
    <!-- Particles Container -->
    <div v-if="enableParticles" id="particles-js" class="particles-container"></div>
    <div class="banner__data__two position-relative">
      <div class="banner-carousel owl-theme owl-carousel owl-dots-none">
        <div 
          v-for="(slide, index) in slides" 
          :key="index"
          class="slide-item position-relative"
        >
          <div
            class="image-layer position-absolute"
            :style="`background-image: url('${slide.backgroundImage}')`"
          ></div>
          <div class="auto-container">
            <div class="row">
              <div class="col-lg-6 col-md-12">
                <div class="content-box position-relative">
                  <div class="sub__title">
                    <h4 class="position-relative">
                      <span class="slider-text-anim two">{{ slide.subtitle }}</span>
                    </h4>
                  </div>
                  <h2 class="position-relative">
                    <span 
                      v-for="(titleLine, titleIndex) in slide.titleLines" 
                      :key="titleIndex"
                      class="slider-text-anim"
                      :class="{ 'title__last': titleIndex === slide.titleLines.length - 1 }"
                    >
                      {{ titleLine.text }}
                      <span v-if="titleLine.highlight">{{ titleLine.highlight }}</span>
                    </span>
                  </h2>
                  <p>{{ slide.description }}</p>
                  <div class="btn-box">
                    <router-link
                      :to="slide.buttonLink"
                      class="theme-btn theme-btn-one"
                    >
                      <i class="icon-02"></i> {{ slide.buttonText }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="banner__bottom">
            <div class="row">
              <div class="col-lg-6 col-md-12"></div>
              <div class="col-lg-6 col-md-12">
                <div class="banner__mission__block flex-column flex-md-row">
                  <div class="mission__block">
                    <h3>{{ missionVision.vision.title }}</h3>
                    <p>{{ missionVision.vision.description }}</p>
                  </div>
                  <div class="mission__block">
                    <h3>{{ missionVision.mission.title }}</h3>
                    <p>{{ missionVision.mission.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { onMounted } from "vue";

export default {
  name: "HeroCarouselComp",
  props: {
    slides: {
      type: Array,
      required: true,
      default: () => []
    },
    backgroundImage: {
      type: String,
      default: '/banner/banner-two.jpg'
    },
    patternImage: {
      type: String,
      default: '/shapes/shape-06.png'
    },
    enableParticles: {
      type: Boolean,
      default: false
    },
    missionVision: {
      type: Object,
      required: true,
      default: () => ({
        vision: { title: 'Our Vision', description: 'Default vision description' },
        mission: { title: 'Our Mission', description: 'Default mission description' }
      })
    }
  },
  setup(props) {
    onMounted(async () => {
      // Import and initialize Owl Carousel
      try {
        await import("owl.carousel");

        // Initialize Owl Carousel for banner
        setTimeout(() => {
          if (window.$ && window.$.fn.owlCarousel) {
            window.$(".banner-carousel").owlCarousel({
              loop: true,
              margin: 0,
              nav: false,
              dots: false,
              smartSpeed: 1000,
              autoplay: 6000,
              autoplayTimeout: 6000,
              autoplayHoverPause: true,
              responsive: {
                0: {
                  items: 1,
                },
                600: {
                  items: 1,
                },
                768: {
                  items: 1,
                },
                992: {
                  items: 1,
                },
                1200: {
                  items: 1,
                },
              },
            });
          }
        }, 100);
      } catch (error) {
        console.error("Error loading Owl Carousel:", error);
      }

      // Initialize particles if enabled
      if (props.enableParticles) {
        setTimeout(() => {
          initParticles();
        }, 1000);
      }
    });

    const initParticles = () => {
      // Wait for particles.js to load
      const checkParticles = () => {
        console.log("Checking for particles.js...", window.particlesJS);

        if (typeof window !== "undefined" && window.particlesJS) {
          console.log("Particles.js found, initializing...");

          // Check if the particles container exists
          const particlesContainer = document.getElementById("particles-js");
          if (!particlesContainer) {
            console.error("Particles container not found!");
            return;
          }

          console.log("Particles container found:", particlesContainer);

          // Clear any existing particles
          particlesContainer.innerHTML = "";

          window.particlesJS("particles-js", {
            particles: {
              number: {
                value: 80,
                density: {
                  enable: true,
                  value_area: 800,
                },
              },
              color: {
                value: "#F6EC4F",
              },
              shape: {
                type: "circle",
                stroke: {
                  width: 0,
                  color: "#000000",
                },
                polygon: {
                  nb_sides: 5,
                },
                image: {
                  src: "img/github.svg",
                  width: 100,
                  height: 100,
                },
              },
              opacity: {
                value: 0.5,
                random: false,
                anim: {
                  enable: false,
                  speed: 1,
                  opacity_min: 0.1,
                  sync: false,
                },
              },
              size: {
                value: 3,
                random: true,
                anim: {
                  enable: false,
                  speed: 40,
                  size_min: 0.1,
                  sync: false,
                },
              },
              line_linked: {
                enable: true,
                distance: 150,
                color: "#F6EC4F",
                opacity: 0.4,
                width: 1,
              },
              move: {
                enable: true,
                speed: 6,
                direction: "none",
                random: false,
                straight: false,
                out_mode: "out",
                bounce: false,
                attract: {
                  enable: false,
                  rotateX: 600,
                  rotateY: 1200,
                },
              },
            },
            interactivity: {
              detect_on: "canvas",
              events: {
                onhover: {
                  enable: true,
                  mode: "repulse",
                },
                onclick: {
                  enable: true,
                  mode: "push",
                },
                resize: true,
              },
              modes: {
                grab: {
                  distance: 400,
                  line_linked: {
                    opacity: 1,
                  },
                },
                bubble: {
                  distance: 400,
                  size: 40,
                  duration: 2,
                  opacity: 8,
                  speed: 3,
                },
                repulse: {
                  distance: 200,
                  duration: 0.4,
                },
                push: {
                  particles_nb: 4,
                },
                remove: {
                  particles_nb: 2,
                },
              },
            },
            retina_detect: true,
          });

          console.log("Particles initialized successfully!");
        } else {
          console.log("Particles.js not found, retrying in 200ms...");
          // Retry after a short delay if particles.js is not loaded yet
          setTimeout(checkParticles, 200);
        }
      };

      // Start checking after a small delay to ensure DOM is ready
      setTimeout(checkParticles, 500);

      // Fallback: Load particles.js dynamically if not available after 3 seconds
      setTimeout(() => {
        if (!window.particlesJS) {
          console.log("Loading particles.js dynamically...");
          const script = document.createElement("script");
          script.src =
            "https://cdn.jsdelivr.net/particles.js/2.0.0/particles.min.js";
          script.onload = () => {
            console.log("Particles.js loaded dynamically, initializing...");
            setTimeout(checkParticles, 100);
          };
          document.head.appendChild(script);
        }
      }, 3000);
    };
  },
};
</script>

<style scoped>
/* Import Google Fonts */
@import url("https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap");

/* Custom button styling for carousel */
.banner__two .btn-box .theme-btn {
  padding: 12px 30px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  border-radius: 0 !important;
  background: linear-gradient(219deg, #fb0e76 0%, #910694 100%) !important;
  color: #fff !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s ease !important;
}

.banner__two .btn-box .theme-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4) !important;
  color: #fff !important;
  text-decoration: none !important;
}

.banner__two .btn-box .theme-btn i {
  font-size: 12px !important;
  transition: transform 0.3s ease !important;
}

.banner__two .btn-box .theme-btn:hover i {
  transform: translateX(3px) !important;
}

/* Mission/Vision block styling */
.banner__mission__block {
  border-radius: 10px 0px 0px 0px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-top: 20px;
}

.mission__block {
  padding: 40px 50px;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  flex: 1;
}

.mission__block:last-child {
  border-right: none;
}

.mission__block h3 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: "Archivo", sans-serif;
}

.mission__block p {
  color: #ccc;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  font-family: "Open Sans", sans-serif;
}

/* Reduce banner height */
section.banner__two {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}

.banner__two .auto-container {
  padding-top: 100px !important;
  padding-bottom: 0px !important;
}

.banner__two .content-box {
  padding-bottom: 60px !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .banner__two .content-box h2 {
    font-size: 50px !important;
    line-height: 60px !important;
  }

  .mission__block {
    padding: 30px 25px !important;
  }

  .mission__block h3 {
    font-size: 18px !important;
  }

  .mission__block p {
    font-size: 13px !important;
    line-height: 22px !important;
  }
}

/* Particles Container */
.particles-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
  overflow: hidden;
}

.particles-container canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
}

/* Import Flaticon */
@import "@/assets/scss/Flaticon.scss";
</style>
